"use client";

import React from "react";
import { Calendar, Tag, Clock, ArrowRight, Share2 } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";


function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="26242" className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="h-[400px] animate-pulse rounded-lg bg-muted/30" />
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) {
    return null; 
  }

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="group overflow-hidden border-border bg-card transition-all duration-300 hover:shadow-lg hover:border-secondary/30">
          <div className="aspect-[16/10] overflow-hidden bg-muted">
            {post.featuredImage ? (
              <img
                src={post.featuredImage}
                alt={post.title}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                <span className="text-sm font-medium">No Image Available</span>
              </div>
            )}
          </div>
          <CardHeader className="p-5 pb-2">
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-secondary">
              {post.categories?.[0] && (
                <span className="uppercase tracking-widest">{post.categories[0].name}</span>
              )}
              {post.date && (
                <>
                  <span className="text-muted-foreground">•</span>
                  <span className="text-muted-foreground font-normal">{new Date(post.date).toLocaleDateString()}</span>
                </>
              )}
            </div>
            <CardTitle className="line-clamp-2 text-lg font-bold leading-snug tracking-tight">
              <Link to={post.link || "#"} className="hover:text-secondary transition-colors">
                {post.title}
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 pt-2">
            <CardDescription className="line-clamp-3 text-sm text-muted-foreground leading-relaxed">
              <div dangerouslySetInnerHTML={{ __html: post.excerpt || "" }} />
            </CardDescription>
          </CardContent>
          <CardFooter className="p-5 pt-0">
            <Link to={post.link || "#"} className="w-full">
              <Button variant="outline" className="w-full justify-between border-border hover:bg-secondary hover:text-secondary-foreground transition-all">
                Read Article <ArrowRight className="size-4" />
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}


function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-8 p-6">
        <div className="h-12 w-3/4 animate-pulse rounded-lg bg-muted/30" />
        <div className="aspect-video w-full animate-pulse rounded-xl bg-muted/30" />
        <div className="space-y-4">
          <div className="h-4 w-full animate-pulse rounded bg-muted/30" />
          <div className="h-4 w-full animate-pulse rounded bg-muted/30" />
          <div className="h-4 w-2/3 animate-pulse rounded bg-muted/30" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 text-center">
        <h2 className="text-2xl font-bold text-destructive">Error Loading Insight</h2>
        <p className="text-muted-foreground">{error}</p>
        <Button onClick={() => refetch()} variant="secondary">Try Again</Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 text-center">
        <h2 className="text-2xl font-bold">Article Not Found</h2>
        <p className="text-muted-foreground">The resource you are looking for is no longer available.</p>
        <Link to="#">
          <Button variant="secondary">Return Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  
  
  const categoryIds = post.categories?.map((c) => c.id) || [];
  const relatedQuery = new WP_Query({
    posts_per_page: 3,
    post__not_in: [Number(post.id)], 
    category__in: categoryIds.length > 0 ? categoryIds : undefined,
    post_type: "post",
    orderby: "date",
    order: "DESC",
  });

  return (
    <article className="animate-in fade-in slide-in-from-bottom-4 duration-1000">
      
      <header className="relative mb-12 flex flex-col items-center text-center">
        <div className="mb-6 flex flex-wrap items-center justify-center gap-3 text-sm font-semibold text-muted-foreground">
          {post.categories?.map((cat, i) => (
            <span key={i} data-index={i} className="flex items-center gap-1 rounded-full bg-secondary/5 px-4 py-1.5 text-secondary border border-secondary/10">
              <Tag className="size-3" />
              {cat.name}
            </span>
          ))}
          <span className="flex items-center gap-1 px-2">
            <Calendar className="size-3" />
            {post.date ? new Date(post.date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : "Unknown Date"}
          </span>
          <span className="flex items-center gap-1 px-2">
            <Clock className="size-3" />
            4 min read
          </span>
        </div>

        <h1 className="max-w-4xl text-4xl font-bold tracking-tight text-foreground md:text-5xl lg:text-6xl leading-[1.1]">
          {post.title}
        </h1>
      </header>

      
      {post.featuredImage && (
        <div className="mb-16 overflow-hidden rounded-2xl border border-border bg-muted shadow-xl">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="h-auto w-full object-cover"
          />
        </div>
      )}

      
      <div className="mx-auto max-w-3xl">
        <div 
          className="prose prose-lg max-w-none 
            prose-headings:font-bold prose-headings:tracking-tight prose-headings:text-primary 
            prose-p:text-muted-foreground prose-p:leading-relaxed
            prose-a:text-secondary prose-a:font-semibold prose-a:no-underline prose-a:border-b-2 prose-a:border-secondary/20 hover:prose-a:border-secondary transition-all
            prose-strong:text-foreground prose-strong:font-bold
            prose-blockquote:border-l-primary prose-blockquote:bg-muted/30 prose-blockquote:py-4 prose-blockquote:pl-8 prose-blockquote:italic prose-blockquote:rounded-r-lg
            prose-img:rounded-xl prose-img:shadow-md prose-img:border prose-img:border-border
            prose-li:text-muted-foreground"
          dangerouslySetInnerHTML={{ __html: post.content || "" }} 
        />

        
        <div className="mt-12 flex flex-col gap-6 border-t border-border pt-8 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap gap-2">
            {post.tags?.map((tag, i) => (
              <span key={i} data-index={i} className="text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-secondary cursor-pointer transition-colors px-2 py-1 bg-muted rounded">
                #{tag.name}
              </span>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-bold text-muted-foreground uppercase tracking-widest">Share:</span>
            <Button variant="outline" size="icon" className="rounded-full border-border hover:bg-secondary hover:text-secondary-foreground transition-all">
              <Share2 className="size-4" />
            </Button>
            <Button variant="outline" size="icon" className="rounded-full border-border hover:bg-secondary hover:text-secondary-foreground transition-all">
              <svg className="size-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" /></svg>
            </Button>
            <Button variant="outline" size="icon" className="rounded-full border-border hover:bg-secondary hover:text-secondary-foreground transition-all">
              <svg className="size-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.468 2.373c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" /></svg>
            </Button>
          </div>
        </div>
      </div>

      
      <section className="mt-24 border-t border-border pt-16">
        <div className="mb-10 flex items-center justify-between">
          <h2 className="text-3xl font-bold tracking-tight text-foreground">Expert Insights</h2>
          <Link to="#">
            <Button variant="ghost" className="hidden md:inline-flex font-bold text-secondary hover:bg-secondary/5">View All Insights <ArrowRight className="ml-2 size-4" /></Button>
          </Link>
        </div>
        
        <PostsProvider wp_query={relatedQuery}>
          <RelatedPostsConsumer />
        </PostsProvider>
        
        <div className="mt-8 text-center md:hidden">
          <Link to="#">
            <Button variant="outline" className="w-full border-border font-bold">View All Insights</Button>
          </Link>
        </div>
      </section>
    </article>
  );
}


export default function SinglePostTemplateSection() {
  
  
  const currentWPQueryParams = wvcClient.getCurrentWPQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-background py-16 md:py-24 selection:bg-secondary/10 selection:text-secondary">
      <div className="container mx-auto px-4">
        <PostsProvider wp_query={wp_query}>
          <SinglePostConsumer />
        </PostsProvider>
      </div>
    </section>
  );
}